<template>
  <div class="search-form">
    <div class="row">
      <div class="col-12">
        <div class="page-header">
          <button
            v-if="allowExport"
            @click="exportCsv"
            type="button"
            class="btn btn-info float-right mx-1 my-1">
            <i class="fa fa-download" />
            Export {{ databaseItems(options.permalink).searchForm.plural }}
          </button>

          <button
            v-if="allowNewObject"
            @click="newObject($event)"
            type="button"
            class="btn btn-primary float-right mx-1 my-1">
            <i class="fa fa-plus" />
            New {{ databaseItems(options.permalink).searchForm.singular }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="showSearch" class="row">
      <div class="col">
        <div @click="toggleSearch()" v-show="isSearchHidden">
          <br/>
          <p class="form-control text-muted fake-input interactive-cursor">
            Filter...
            <span class="fa fa-search float-right"></span>
          </p>
        </div>
        <div v-show="!isSearchHidden">
          <vue-form
            ref="form"
            :sections="databaseSearchForm(options.permalink).fields"
            :options="{
              submit: 'Search',
              submitting: 'Searching...',
              permalink: options.permalink,
              method: 'search'
            }">
            <span slot="title">
              <i class="fa fa-search fa-fw"></i> Search {{ databaseSearchForm(options.permalink).plural }}
              <span class="fa fa-times float-right fa-lg text-danger interactive-cursor" @click="toggleSearch()"></span>
            </span>
            <div class="form-group" slot="actions">
              <div class="col-sm-8 col-sm-offset-4">
                <button class="btn btn-success" type="button" @click="refresh()">Search</button>
              </div>
            </div>
          </vue-form>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12">
        <div class="alert alert-info" v-if="!databaseItems(options.permalink).count">
          <i class="far fa-meh fa-fw fa-lg"></i> {{ databaseSearchForm(options.permalink).noResults }}
        </div>
        <template v-else>
          <!-- <button
            class="btn btn-default"
            v-if="allowToggle"
            @click="$emit('toggleDisplay')">
            <span
              class="fa fa-fw"
              :class="!tableDisplay ? 'fa-bars' : 'fa-table'">
            </span>
            Toggle Display
          </button> -->
          <paging-input ref="paging" v-if="showPaging"
            @change="changePage"
            :options="{
              current: (databaseItems(options.permalink).paging.offset + parseInt(databaseItems(options.permalink).paging.limit)) / parseInt(databaseItems(options.permalink).paging.limit),
              totalResults: databaseItems(options.permalink).count,
              limit: parseInt(databaseItems(options.permalink).paging.limit)
            }">
          </paging-input>
          <responsive-table
            @orderColumn="sort($event)"
            :hideTitle="true"
            :table="{ columns: databaseItems(options.permalink).columns }"
            :default-sort-column="sortColumn"
            :default-sort="sortDirection"
            v-if="tableDisplay">
            <template slot="rows">
              <template v-for="(row, i) in databaseItems(options.permalink).rows">
                <component
                  @edit="editObject($event)"
                  @deleteObject="deleteObject($event)"
                  @reinstateObject="reinstateObject($event)"
                  :is="parseType(options.permalink) + '-row'"
                  :object="row"
                  :index="i"
                  :key="i">
                </component>
              </template>
            </template>
          </responsive-table>
          <div class="row" v-else>
            <template v-for="(object, i) in databaseItems(options.permalink).rows">
                <div class="col" v-if="isNewDay(object, i) && showDates" :key="i + 'date'">
                  <h3 class="border border-dark border-top-0 border-left-0 border-right-0 pb-2">
                    {{ object.createdAt | date_format }}
                  </h3>
                </div>
                <component 
                  :key="i" :is="parseType(options.permalink) + '-card'"
                  :object="object"
                  :options="options">
                </component>
            </template>
          </div>
          <paging-input ref="paging" v-if="showPaging"
            @change="changePage"
            :options="{
              current: (databaseItems(options.permalink).paging.offset + parseInt(databaseItems(options.permalink).paging.limit)) / parseInt(databaseItems(options.permalink).paging.limit),
              totalResults: databaseItems(options.permalink).count,
              limit: parseInt(databaseItems(options.permalink).paging.limit)
            }">
          </paging-input>
        </template>
      </div>
    </div>
  </div>
</template>


<script>
  import PagingInput from '@/components/inputs/PagingInput'
  import ResponsiveTable from '@/components/ui/ResponsiveTable'
  import VueForm from '@/components/Form'
  import LiveFeedCard from '@/components/cards/LiveFeed'
  import WorkSummaryCard from '@/components/cards/WorkSummary.vue'
  import CompaniesRow from '@/components/rows/CompanyRow.vue'
  import ClientRow from '@/components/rows/ClientRow.vue'
  import CompanyManagersRow from '@/components/rows/CompanyManagersRow.vue'
  import StaffRow from '@/components/rows/StaffRow.vue'
  import ContractorRow from '@/components/rows/ContractorRow.vue'
  import ReferralCodeRow from '@/components/rows/ReferralCodeRow.vue'
  import AssignedQuotesRow from '@/components/rows/AssignedQuoteRow.vue'
  import {
    mapGetters
  } from 'vuex'
  import {
    LOAD_DATA,
    EDIT_DATABASE_OBJECT,
    DELETE_DATABASE_OBJECT,
    REINSTATE_DATABASE_OBJECT,
    NEW_DATABASE_OBJECT
  } from '@/vuex/types'

  export default {
    name: 'SearchForm',
    components: {
      PagingInput,
      ResponsiveTable,
      VueForm,
      LiveFeedCard,
      CompaniesRow,
      ClientRow,
      StaffRow,
      ContractorRow,
      ReferralCodeRow,
      WorkSummaryCard,
      AssignedQuotesRow,
      'companyContractors-row': ContractorRow,
      CompanyManagersRow
    },
    props: {
      options: {
        type: Object,
        default () {
          return {
            permalink: false,
            title: 'Loading Form...',
            submit: 'Loading...',
            submitting: 'Processing...',
            confirmation: false,
            redirect: '/',
            params: false
          }
        }
      },
      tableDisplay: {
        type: Boolean,
        default () {
          return false
        }
      },
      showDates: {
        type: Boolean,
        default () {
          return false
        }
      },
      showSearch: {
        type: Boolean,
        default () {
          return false
        }
      },
      searchHidden: {
        type: Boolean,
        default () {
          return false
        }
      },
      allowExport: {
        type: Boolean,
        default () {
          return false
        }
      },
      allowNewObject: {
        type: Boolean,
        default () {
          return false
        }
      },
      redirectUrl: {
        type: String,
        default () {
          return null
        }
      },
      showPaging: {
        type: Boolean,
        default () {
          return false
        }
      }
    },
    data () {
      return {
        sortColumn: false,
        sortDirection: false,
        searchForm: [],
        searchOffset: 0,
        searchLimit: 25,
        newSearch: true,
        isSearchHidden: false,
        pagingOptions: {
          totalResults: 10,
          limit: 10,
          current: 1
        },
        newPermalink: false
      }
    },
    computed: {
      ...mapGetters([
        'databaseItems',
        'databaseSearchForm'
      ])
    },
    mounted () {
      this.isSearchHidden = this.searchHidden
      this.createNewSearch()
    },
    watch: {
      $route () {
        this.createNewSearch()
      }
    },
    methods: {
      async exportCsv () {
        await this.$nextTick()
        this.$refs.exportForm.submit()
      },
      isNewDay (object, index) {
        if (index === 0) {
          return true
        } else if (moment(object.createdAt).format('DD/MM/YYYY') === moment(this.databaseItems(this.options.permalink).rows[index - 1].createdAt).format('DD/MM/YYYY')) {
          return false
        } else {
          return true
        }
      },
      sort (column) {
        this.sortColumn = column.name
        this.sortDirection = column.sortDirection
        this.search()
      },
      async createNewSearch () {
        let searchForm = this.databaseSearchForm(this.options.permalink)
        let limit = false
        if (searchForm) {
          limit = searchForm.fields.splice(-1, 1)
        }
        this.searchOffset = 0
        let loadData = {
          permalink: this.options.permalink,
          routeQuery: this.$route.query,
          params: this.options.params,
          searchForm,
          offset: this.searchOffset,
          limit: limit ? limit[0].value : this.databaseItems(this.options.permalink).paging.limit,
          newSearch: true,
          sortColumn: this.sortColumn,
          sortDirection: this.sortDirection
        }
        await this.$store.dispatch(LOAD_DATA, loadData)
      },
      async refresh (resetOffset = true) {
        if (resetOffset) this.searchOffset = 0
        await this.search()
      },
      async search () {
        await this.$refs.form.validateForm()
        if (this.$refs.form.validForm) {
          let searchForm = this.databaseSearchForm(this.options.permalink)
          let limit = searchForm.fields.splice(-1, 1)
          await this.$store.dispatch(LOAD_DATA, {
            permalink: this.options.permalink,
            params: this.options.params,
            searchForm,
            routeQuery: this.$route.query,
            offset: this.searchOffset,
            limit: limit[0].value,
            newSearch: this.newSearch,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection
          })
        }
      },
      async toggleSearch () {
        this.isSearchHidden = !this.isSearchHidden
        if (!this.isSearchHidden) {
          await this.$nextTick()
          this.$refs.form.$el.querySelectorAll('input[type=text]')[0].focus()
        }
      },
      async changePage (page) {
        this.searchOffset = (page - 1) * this.databaseItems(this.options.permalink).paging.limit
        await this.search()
      },
      parseType (type) {
        switch (type) {
          default:
            return type
        }
      },
      async editObject (payload) {
        await this.$store.dispatch(EDIT_DATABASE_OBJECT, { ...payload, permalink: this.options.permalink, params: { redirectUrl: this.redirectUrl } })
      },
      async deleteObject (payload) {
        await this.$store.dispatch(DELETE_DATABASE_OBJECT, { ...payload, permalink: this.options.permalink, params: { redirectUrl: this.redirectUrl } })
      },
      async newObject () {
        await this.$store.dispatch(NEW_DATABASE_OBJECT, { permalink: this.options.permalink })
      },
      async reinstateObject (payload) {
        await this.$store.dispatch(REINSTATE_DATABASE_OBJECT, { ...payload, permalink: this.options.permalink, params: { redirectUrl: this.redirectUrl } })
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/SearchForm.vue